import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Bomb, Crown, TrendingDown, Zap, AlertTriangle, Trophy, Skull, ArrowUpDown } from 'lucide-react';
import MLBPlayerAvatar from './MLBPlayerAvatar';
import { cn } from '@/lib/utils';

interface RecapMatchup {
  id: string;
  week: number;
  home_team_id: string;
  away_team_id: string;
  home_team_name: string;
  away_team_name: string;
  home_score: number;
  away_score: number;
  status: string;
  week_start: string;
  week_end: string;
}

interface RecapSabotage {
  id: string;
  matchup_id: string;
  sabotage_name: string;
  sabotage_icon: string;
  effect_type: string;
  effect_config: any;
  applied_by_team_id: string;
  target_team_id: string;
  cost: number;
}

interface TopPerformer {
  player_id: string;
  player_name: string;
  position: string;
  team_name: string;
  headshot_url: string | null;
  fantasy_team: string;
  trash_points: number;
}

interface SabotageImpact {
  matchup_id: string;
  home_team: string;
  away_team: string;
  home_score: number;
  away_score: number;
  sabotages: RecapSabotage[];
  estimatedSwing: number;
  winner_changed: boolean;
}

interface WeeklyRecapProps {
  leagueId: string;
  week: number;
}

const WeeklyRecap = ({ leagueId, week }: WeeklyRecapProps) => {
  const [loading, setLoading] = useState(true);
  const [matchups, setMatchups] = useState<RecapMatchup[]>([]);
  const [sabotages, setSabotages] = useState<RecapSabotage[]>([]);
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([]);
  const [teamNames, setTeamNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!leagueId || !week) return;
    fetchRecap();
  }, [leagueId, week]);

  const fetchRecap = async () => {
    setLoading(true);
    try {
      // Fetch matchups, sabotages, teams in parallel
      const [matchupsRes, sabotagesRes, teamsRes] = await Promise.all([
        supabase
          .from('matchups')
          .select('id, week, home_team_id, away_team_id, home_team_name, away_team_name, home_score, away_score, status, week_start, week_end')
          .eq('league_id', leagueId)
          .eq('week', week),
        supabase
          .from('applied_sabotages')
          .select('id, matchup_id, sabotage_name, sabotage_icon, effect_type, effect_config, applied_by_team_id, target_team_id, cost')
          .eq('league_id', leagueId)
          .eq('week', week)
          .eq('status', 'active'),
        supabase
          .from('teams')
          .select('id, name')
          .eq('league_id', leagueId),
      ]);

      const mData = (matchupsRes.data || []).map((m: any) => ({
        ...m,
        home_score: Number(m.home_score),
        away_score: Number(m.away_score),
      }));
      setMatchups(mData);
      setSabotages(sabotagesRes.data || []);

      const names: Record<string, string> = {};
      for (const t of teamsRes.data || []) names[t.id] = t.name;
      setTeamNames(names);

      // Fetch top performers: get all starters across all matchup teams
      if (mData.length > 0) {
        const allTeamIds = mData.flatMap((m: RecapMatchup) => [m.home_team_id, m.away_team_id]);
        const { data: rosters } = await supabase
          .from('roster_assignments')
          .select('team_id, player_id, is_starter, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
          .in('team_id', allTeamIds)
          .eq('is_starter', true);

        if (rosters && rosters.length > 0) {
          const playerIds = rosters.map((r: any) => r.player_id);
          const weekStart = mData[0].week_start;
          const weekEnd = mData[0].week_end;

          const { data: stats } = await supabase
            .from('player_game_stats')
            .select('player_id, trash_points')
            .in('player_id', playerIds)
            .gte('game_date', weekStart)
            .lte('game_date', weekEnd);

          // Aggregate
          const pointsByPlayer: Record<string, number> = {};
          for (const s of stats || []) {
            pointsByPlayer[s.player_id] = (pointsByPlayer[s.player_id] || 0) + Number(s.trash_points);
          }

          const performers: TopPerformer[] = rosters.map((r: any) => ({
            player_id: r.player_id,
            player_name: r.players.full_name,
            position: r.players.position_abbreviation,
            team_name: r.players.mlb_team_abbreviation || '',
            headshot_url: r.players.headshot_url,
            fantasy_team: names[r.team_id] || 'Unknown',
            trash_points: pointsByPlayer[r.player_id] || 0,
          }));

          performers.sort((a, b) => b.trash_points - a.trash_points);
          setTopPerformers(performers);
        }
      }
    } catch (err) {
      console.error('Weekly recap fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Derived data
  const finalMatchups = useMemo(() => matchups.filter(m => m.status === 'final'), [matchups]);

  const upsets = useMemo(() => {
    // An "upset" = the losing team had more total season points (better overall record) — simplified: larger margin loss
    // Simple heuristic: biggest margin of victory
    return [...finalMatchups]
      .map(m => ({
        ...m,
        margin: Math.abs(m.home_score - m.away_score),
        winner: m.home_score > m.away_score ? m.home_team_name : m.away_team_name,
        loser: m.home_score > m.away_score ? m.away_team_name : m.home_team_name,
        winnerScore: Math.max(m.home_score, m.away_score),
        loserScore: Math.min(m.home_score, m.away_score),
      }))
      .sort((a, b) => b.margin - a.margin);
  }, [finalMatchups]);

  const closestMatchup = useMemo(() => {
    if (finalMatchups.length === 0) return null;
    return [...finalMatchups].sort((a, b) =>
      Math.abs(a.home_score - a.away_score) - Math.abs(b.home_score - b.away_score)
    )[0];
  }, [finalMatchups]);

  const sabotageImpacts = useMemo((): SabotageImpact[] => {
    const impactMap = new Map<string, SabotageImpact>();
    for (const sab of sabotages) {
      if (!sab.matchup_id) continue;
      const m = matchups.find(mx => mx.id === sab.matchup_id);
      if (!m) continue;

      if (!impactMap.has(sab.matchup_id)) {
        impactMap.set(sab.matchup_id, {
          matchup_id: sab.matchup_id,
          home_team: m.home_team_name,
          away_team: m.away_team_name,
          home_score: m.home_score,
          away_score: m.away_score,
          sabotages: [],
          estimatedSwing: 0,
          winner_changed: false,
        });
      }
      const impact = impactMap.get(sab.matchup_id)!;
      impact.sabotages.push(sab);

      // Estimate swing based on effect type
      const cfg = sab.effect_config || {};
      if (sab.effect_type === 'score_modifier') {
        impact.estimatedSwing += Math.abs(Number(cfg.flat_points || 0));
      } else if (sab.effect_type === 'score_multiplier') {
        const mult = Number(cfg.multiplier || 1);
        const avgScore = (m.home_score + m.away_score) / 2;
        impact.estimatedSwing += Math.abs(avgScore * (mult - 1));
      } else if (sab.effect_type === 'stat_multiplier') {
        impact.estimatedSwing += 5; // rough estimate
      }
    }

    // Check if margin < estimated swing (sabotage may have changed outcome)
    for (const impact of impactMap.values()) {
      const margin = Math.abs(impact.home_score - impact.away_score);
      if (impact.estimatedSwing >= margin && margin > 0) {
        impact.winner_changed = true;
      }
    }

    return [...impactMap.values()].sort((a, b) => b.estimatedSwing - a.estimatedSwing);
  }, [matchups, sabotages]);

  // Most benefited: team that gained the most from sabotages applied against opponents
  const sabotageBeneficiary = useMemo(() => {
    const teamGains: Record<string, { name: string; count: number; cost: number }> = {};
    for (const sab of sabotages) {
      const applier = sab.applied_by_team_id;
      if (!teamGains[applier]) {
        teamGains[applier] = { name: teamNames[applier] || 'Unknown', count: 0, cost: 0 };
      }
      teamGains[applier].count++;
      teamGains[applier].cost += sab.cost;
    }
    const sorted = Object.entries(teamGains).sort((a, b) => b[1].count - a[1].count);
    return sorted.length > 0 ? { teamId: sorted[0][0], ...sorted[0][1] } : null;
  }, [sabotages, teamNames]);

  // Unluckiest: lost by smallest margin with sabotage on their matchup
  const unluckiest = useMemo(() => {
    const sabotageMatchupIds = new Set(sabotages.map(s => s.matchup_id));
    const losers = finalMatchups
      .filter(m => sabotageMatchupIds.has(m.id))
      .map(m => {
        const margin = Math.abs(m.home_score - m.away_score);
        const loserName = m.home_score < m.away_score ? m.home_team_name : m.away_team_name;
        const loserScore = Math.min(m.home_score, m.away_score);
        const winnerScore = Math.max(m.home_score, m.away_score);
        return { matchup: m, loserName, margin, loserScore, winnerScore };
      })
      .sort((a, b) => a.margin - b.margin);
    return losers.length > 0 ? losers[0] : null;
  }, [finalMatchups, sabotages]);

  const top5 = topPerformers.slice(0, 5);
  const bottom5 = [...topPerformers].sort((a, b) => a.trash_points - b.trash_points).slice(0, 3);

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }

  if (finalMatchups.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-foreground">Week {week} isn't complete yet</p>
          <p className="text-sm mt-1">Check back after all matchups are finalized for the full recap.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Trophy className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold font-heading text-foreground">Week {week} Recap</h2>
        <Badge className="bg-accent text-accent-foreground text-[10px]">FINAL</Badge>
      </div>

      {/* Sabotage Impact Section */}
      {sabotageImpacts.length > 0 && (
        <Card className="bg-card border-damage/30 overflow-hidden">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-heading flex items-center gap-2 text-damage">
              <Bomb className="w-4 h-4" />
              Sabotage Impact Report
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            {sabotageImpacts.map((impact) => (
              <div key={impact.matchup_id} className="rounded-lg bg-muted/30 border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">
                    {impact.away_team} vs {impact.home_team}
                  </span>
                  {impact.winner_changed && (
                    <Badge className="bg-damage/20 text-damage border-damage/30 text-[9px] gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Outcome Changed!
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {impact.away_score.toFixed(1)} – {impact.home_score.toFixed(1)}
                  <span className="ml-2 text-damage">
                    ~{impact.estimatedSwing.toFixed(1)} pts swung by sabotage
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {impact.sabotages.map((sab) => (
                    <Badge
                      key={sab.id}
                      variant="outline"
                      className="text-[9px] gap-1 border-damage/40 text-damage/90"
                    >
                      <span>{sab.sabotage_icon}</span>
                      {sab.sabotage_name}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}

            {/* Sabotage MVP + Unluckiest */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {sabotageBeneficiary && (
                <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Crown className="w-3.5 h-3.5 text-primary" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">Sabotage MVP</span>
                  </div>
                  <p className="text-sm font-bold text-foreground">{sabotageBeneficiary.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {sabotageBeneficiary.count} sabotage{sabotageBeneficiary.count > 1 ? 's' : ''} deployed • ${sabotageBeneficiary.cost} spent
                  </p>
                </div>
              )}
              {unluckiest && (
                <div className="rounded-lg bg-damage/5 border border-damage/20 p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Skull className="w-3.5 h-3.5 text-damage" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-damage">Unluckiest</span>
                  </div>
                  <p className="text-sm font-bold text-foreground">{unluckiest.loserName}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Lost by {unluckiest.margin.toFixed(1)} pts in a sabotaged matchup
                  </p>
                </div>
              )}
            </div>

            {/* CTA to use sabotages */}
            <div className="rounded-lg bg-gradient-to-r from-damage/10 to-primary/10 border border-damage/20 p-3 text-center">
              <p className="text-xs font-semibold text-foreground">💣 Don't get caught without ammo</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Sabotages affected {sabotageImpacts.length} matchup{sabotageImpacts.length > 1 ? 's' : ''} this week.
                {sabotageImpacts.some(i => i.winner_changed) && ' Some even changed the outcome!'}
                {' '}Spin the wheel before your next matchup.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {sabotages.length === 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <Bomb className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No sabotages deployed this week</p>
            <p className="text-xs text-muted-foreground mt-1">
              💣 Nobody used sabotages. That's free chaos left on the table! Spin the wheel next week.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Top Trash Performers */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            Top Trash Performers
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="space-y-2">
            {top5.map((p, i) => (
              <div key={p.player_id} className="flex items-center gap-3 py-1.5">
                <span className={cn(
                  "w-5 text-center text-xs font-bold",
                  i === 0 ? "text-primary" : "text-muted-foreground"
                )}>
                  {i + 1}
                </span>
                <MLBPlayerAvatar name={p.player_name} headshotUrl={p.headshot_url} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{p.player_name}</p>
                  <p className="text-[10px] text-muted-foreground">{p.position} • {p.team_name}, {p.fantasy_team}</p>
                </div>
                <Badge className={cn(
                  "text-xs font-bold",
                  i === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                )}>
                  {p.trash_points.toFixed(1)} 💩
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Biggest Upset / Closest Matchup */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {upsets.length > 0 && (
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <ArrowUpDown className="w-3.5 h-3.5 text-primary" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Biggest Blowout</span>
              </div>
              <p className="text-sm font-bold text-foreground">{upsets[0].winner}</p>
              <p className="text-xs text-muted-foreground">
                defeated {upsets[0].loser} by {upsets[0].margin.toFixed(1)} pts
              </p>
              <p className="text-lg font-bold font-heading text-primary mt-1">
                {upsets[0].winnerScore.toFixed(1)} – {upsets[0].loserScore.toFixed(1)}
              </p>
            </CardContent>
          </Card>
        )}

        {closestMatchup && (
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingDown className="w-3.5 h-3.5 text-damage" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nail-Biter</span>
              </div>
              <p className="text-sm font-bold text-foreground">
                {closestMatchup.away_team_name} vs {closestMatchup.home_team_name}
              </p>
              <p className="text-xs text-muted-foreground">
                Decided by just {Math.abs(closestMatchup.home_score - closestMatchup.away_score).toFixed(1)} pts
              </p>
              <p className="text-lg font-bold font-heading text-foreground mt-1">
                {closestMatchup.away_score.toFixed(1)} – {closestMatchup.home_score.toFixed(1)}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Anti-Performers (worst trash output) */}
      {bottom5.length > 0 && bottom5[0].trash_points < top5[top5.length - 1]?.trash_points && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-heading flex items-center gap-2 text-muted-foreground">
              <TrendingDown className="w-4 h-4" />
              Least Trashy (Talent Alert 🚨)
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-2">
              {bottom5.map((p, i) => (
                <div key={p.player_id} className="flex items-center gap-3 py-1">
                  <MLBPlayerAvatar name={p.player_name} headshotUrl={p.headshot_url} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{p.player_name}</p>
                    <p className="text-[10px] text-muted-foreground">{p.position} • {p.fantasy_team}</p>
                  </div>
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    {p.trash_points.toFixed(1)}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WeeklyRecap;
